import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const LoadTimesStatistics = () => {
  const [loadTimes, setLoadTimes] = useState([]);
  const [averageLoadTimeByDay, setAverageLoadTimeByDay] = useState({});
  const [loadCountByDay, setLoadCountByDay] = useState({});
  const [selectedDay, setSelectedDay] = useState(
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' })
  );
  const [loadTimesForSelectedDay, setLoadTimesForSelectedDay] = useState([]);
  const [lastTenDaysData, setLastTenDaysData] = useState([]);

  useEffect(() => {
    const fetchLoadTimes = async () => {
      const loadTimesRef = collection(db, 'loadTimes');
      const querySnapshot = await getDocs(loadTimesRef);

      const loadData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setLoadTimes(loadData);
      processLoadData(loadData);
    };

    const processLoadData = (data) => {
      const dailyLoadTimes = {};

      data.forEach(({ loadTimeMs, timestamp }) => {
        const date = new Date(timestamp.seconds * 1000).toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });

        if (!dailyLoadTimes[date]) {
          dailyLoadTimes[date] = { totalLoadTime: 0, count: 0, loadTimes: [] };
        }

        dailyLoadTimes[date].totalLoadTime += loadTimeMs;
        dailyLoadTimes[date].count += 1;
        dailyLoadTimes[date].loadTimes.push({ loadTimeMs, timestamp: timestamp.seconds });
      });

      const averages = {};
      const counts = {};
      Object.keys(dailyLoadTimes).forEach((day) => {
        averages[day] = dailyLoadTimes[day].totalLoadTime / dailyLoadTimes[day].count;
        counts[day] = dailyLoadTimes[day].count;
      });

      setAverageLoadTimeByDay(averages);
      setLoadCountByDay(counts);

      const lastTenDays = Object.keys(averages)
        .sort((a, b) => new Date(b) - new Date(a))
        .slice(0, 10)
        .map((day) => ({
          day,
          avgLoadTime: averages[day],
          loadCount: counts[day],
        }));
      setLastTenDaysData(lastTenDays);
    };

    fetchLoadTimes();
  }, []);

  useEffect(() => {
    if (selectedDay && loadTimes.length) {
      const filteredTimes = loadTimes
        .filter(({ timestamp }) => {
          // Extract the date in Mexico City time
          const date = new Date(timestamp.seconds * 1000).toLocaleDateString('en-CA', {
            timeZone: 'America/Mexico_City',
          });
          return date === selectedDay;
        })
        .map((item) => ({
          loadTimeMs: item.loadTimeMs,
          parentPage: item.parentPage ? item.parentPage : "",
          url: item.fullUrl ? item.fullUrl : "",
          time: new Date(item.timestamp.seconds * 1000).toLocaleTimeString('en-US', {
            timeZone: 'America/Mexico_City',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
          }),
          deviceType: item.deviceType,
          timestamp: item.timestamp.seconds,
        }))
        .sort((a, b) => b.timestamp - a.timestamp); 
      setLoadTimesForSelectedDay(filteredTimes);
    }
  }, [selectedDay, loadTimes]);

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-4xl mx-auto">
      <h3 className="text-lg font-bold mb-4 text-gray-800">Load Times Statistics</h3>

      {/* Date Selector */}
      <label className="block text-gray-700 text-sm font-bold mb-2">Select Date:</label>
      <input
        type="date"
        className="block w-full p-2 border border-gray-300 rounded-md mb-6"
        value={selectedDay}
        onChange={(e) => setSelectedDay(e.target.value)}
      />

      {/* Average Load Time and Load Count for Selected Day */}
      <div className="mb-6">
        <div className="text-sm text-gray-600">Average Load Time on {selectedDay}</div>
        <div className="text-xl font-bold text-gray-900">
          {averageLoadTimeByDay[selectedDay] ? `${averageLoadTimeByDay[selectedDay].toFixed(2)} ms` : 'N/A'}
        </div>
        <div className="text-sm text-gray-600">Load Count on {selectedDay}</div>
        <div className="text-xl font-bold text-gray-900">{loadCountByDay[selectedDay] || 'N/A'}</div>
      </div>

      {/* 10-Day Summary */}
      <h4 className="font-bold mt-6 text-gray-700">Summary of Last 10 Days</h4>
      <ul className="space-y-2 mt-2 bg-gray-50 rounded-lg p-4">
        {lastTenDaysData.map(({ day, avgLoadTime, loadCount }) => (
          <li key={day} className="p-2 bg-white rounded-md shadow text-gray-800">
            <div className="font-semibold">{day}</div>
            <div>Average Load Time: {avgLoadTime.toFixed(2)} ms</div>
            <div>Load Count: {loadCount}</div>
          </li>
        ))}
      </ul>

      {/* Load Times for Selected Day */}
      {selectedDay && (
        <>
          <h4 className="font-bold mt-6 text-gray-700">Load Times on {selectedDay}</h4>
          <table className="min-w-full bg-white rounded-lg shadow-md mt-4">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b text-left text-gray-700 font-semibold">Time of Load</th>
                <th className="py-2 px-4 border-b text-left text-gray-700 font-semibold">Time to Load (ms)</th>
                <th className="py-2 px-4 border-b text-left text-gray-700 font-semibold">Device Type</th>
                <th className="py-2 px-4 border-b text-left text-gray-700 font-semibold">Origin</th>
                <th className="py-2 px-4 border-b text-left text-gray-700 font-semibold">Full URL</th>
              </tr>
            </thead>
            <tbody>
              {loadTimesForSelectedDay.map(({ loadTimeMs, time, parentPage, url, deviceType }, index) => (
                <tr key={index} className="hover:bg-gray-100">
                  <td className="py-2 px-4 border-b text-gray-800">{time}</td>
                  <td className="py-2 px-4 border-b text-gray-800">{loadTimeMs}</td>
                  <td className="py-2 px-4 border-b text-gray-800">{deviceType || '--'}</td>
                  <td className="py-2 px-4 border-b text-gray-800">{parentPage || '--'}</td>
                  <td className="py-2 px-4 border-b text-gray-800 break-words">{url || '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default LoadTimesStatistics;
